import { useQuery } from 'react-query';
import SearchApi from '../../../../../../features/search/SearchApi';
import { ISearchResult } from '../../../../../../features/search/Entities';
import { useTelemetryContext } from '../../../../../../telemetry/telemetryContext';
import { getUserInfo } from '../../../../../../telemetry/utils';
import { useUserContext } from '../../../../../../shared/context/user-context';

export type SearchQueryResult = {
    isLoading: boolean;
    data: ISearchResult | null;
};

export const superSearchQueryKey = 'super-search';

function searchCancelledWhenNoLongerNeeded(searchPhrase: string): Promise<ISearchResult> {
    let controller = new AbortController();
    let search = SearchApi.search(searchPhrase, controller.signal);
    // React Query calls cancel() when the phrase changes or the search box unmounts.
    (search as Promise<ISearchResult> & { cancel: () => void }).cancel = () => controller.abort();
    return search;
}

/**
 * Searches for the phrase in the search box once typing has paused (`debouncedSearchPhrase` caught up with it),
 * and only ever returns results for exactly that phrase: a search for an earlier phrase is cancelled as soon as
 * the phrase changes.
 */
export function useSearch(searchPhrase: string, debouncedSearchPhrase: string): SearchQueryResult {
    let { logger } = useTelemetryContext();
    let { user } = useUserContext();

    let isSearchable = searchPhrase.length > 2;
    let isTyping = searchPhrase !== debouncedSearchPhrase;
    let phraseToSearch = isSearchable && !isTyping ? searchPhrase : '';

    let { data, isFetching } = useQuery<ISearchResult>(
        [superSearchQueryKey, phraseToSearch],
        () => searchCancelledWhenNoLongerNeeded(phraseToSearch),
        {
            enabled: phraseToSearch !== '',
            // Results depend on who is searching, and impersonation changes that without a reload.
            cacheTime: 0,
            retry: false,
            refetchOnWindowFocus: false,
            onError: (error) => {
                if (logger) {
                    logger.logException(error, {
                        origin: 'AdvancedNavbarSearch LargerScreens perform search',
                        searchPhrase: phraseToSearch,
                        userInfo: getUserInfo(user)
                    });
                }
            }
        }
    );

    return {
        isLoading: isSearchable && (isTyping || isFetching),
        data: phraseToSearch !== '' && data ? data : null
    };
}
